import React, { Suspense, lazy, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import CouponBanner from '../components/CouponBanner';
import Collections from '../components/Collections';
import { useHomepageData } from '../hooks/useHomepageData';
import SEO from '../components/SEO';
import './Home.css';

// Lazy-load heavy below-the-fold components ONLY
// Collections is now loaded immediately for faster homepage paint
const FounderStrip = lazy(() => import('../components/FounderStrip'));
const OccasionEdit = lazy(() => import('../components/OccasionEdit'));

export default function Home() {
  const navigate = useNavigate();
  const [pageReady, setPageReady] = useState(false);

  const {
    products,
    categories,
    loading: homepageLoading,
    error: homepageError
  } = useHomepageData();

  const banners = [];
  const founderVideos = [];

  // DISABLED: Product image preloading removed to reduce initial load time
  // Hero image is already preloaded in index.html
  // Product images will load naturally as user scrolls

  // Trigger fade-in when data arrives
  useEffect(() => {
    if (products.length > 0 || categories.length > 0) {
      setPageReady(true);
    }
  }, [products, categories]);

  return (
    <main className={`home ${pageReady ? 'ready' : ''}`} style={{ minHeight: '60vh' }}>
      <SEO
        title="Premium Silk Sarees Online"
        description="Shop premium handcrafted silk sarees including banarasi, kanjivaram, tussar, and designer sarees. Authentic traditional Indian wear with 7-day easy exchanges. Free shipping on orders above ₹5000."
        url="/"
        keywords="silk sarees, banarasi sarees, kanjivaram sarees, designer sarees, traditional sarees, wedding sarees, festival sarees, handcrafted sarees, Indian wear, Varaha Silks"
        image="/hero1.jpg"
      />
      <HeroCarousel banners={banners} />
      <CouponBanner />

      {/* NO SHIMMER NEEDED - Data is pre-rendered from build! */}

      {/* Collections component - now loaded immediately (not lazy) */}
      <Collections
        categories={categories}
        products={products}
        loading={homepageLoading}
        onViewAllKanjivaram={() => navigate('/shop/kanchipuram')}
        onViewAllBanarasi={() => navigate('/shop/venkatagiri')}
        onViewAllTussar={() => navigate('/shop/mysore')}
        onViewAllPatola={() => navigate('/shop/kalamkari')}
        onViewAllLinen={() => navigate('/shop/kerala')}
        onViewAllWedding={() => navigate('/shop/wedding')}
        onViewAllEngagement={() => navigate('/shop/engagement')}
        onViewAllHaldi={() => navigate('/shop/haldi')}
        onViewAllMehendi={() => navigate('/shop/mehendi')}
        onViewAllBabyShower={() => navigate('/shop/babyshower')}
        onViewAllPoojaRitual={() => navigate('/shop/pooja-ritual')}
        onViewAllFestival={() => navigate('/shop/festival')}
        onViewAllSareeFunction={() => navigate('/shop/saree-function')}
        onExploreFilters={() => navigate('/shop/kanchipuram')}
        onChatWithStylist={() => navigate('/contact')}
        onSelectCollection={(route) => navigate(route)}
      />

      {/* Lazy-load Occasion Edit Section */}
      <Suspense fallback={<div style={{ minHeight: '200px' }} />}>
        <OccasionEdit
          onNavigateToCollections={() => navigate('/collections')}
        />
      </Suspense>

      {/* Lazy-load FounderStrip (YouTube videos) - only show if we have videos */}
      {!homepageLoading && !homepageError && founderVideos.length > 0 && (
        <Suspense fallback={<div style={{ minHeight: '200px' }} />}>
          <FounderStrip
            heading="Shop from our founder"
            subheading="Custom handpicked styles showcased by our founder, experience the quality"
            items={founderVideos}
            autoIntervalMs={3500}
          />
        </Suspense>
      )}
    </main>
  );
}